import { useRef, useState } from 'react'

import { ActStrip, useConfirmAfterApplied } from './ActStrip.jsx'
import { PosterSlot } from './WatchPanel.jsx'
import { MIN_QUERY_LENGTH, useTitleSearch } from './useTitleSearch.js'
import { noNextSeasonFound, subscriptionsOf } from './watch.js'
import { isSelfReferenceHit } from './watchTitle.js'
import {
  KIND_LINK_SEQUEL,
  linkCommand,
  logTargetForSearchHit,
  useWatchRequest,
} from './watchRequests.js'

// V2-6h2 — "Link the next season": the site's half of `watch.link_sequel`
// (V2-6h1, ADR-0009 D6). Jordan, 2026-09-24: "On a title page, Link the
// next season → search → pick. A manual link always wins over the
// automatic one." This replaces `FranchiseNote`'s old `/watch link` hint
// text (`WatchTitle.jsx`) with a real control.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 TWO INDEPENDENT FACTS DECIDE WHAT SHOWS, NEITHER RE-DERIVED HERE
// ═══════════════════════════════════════════════════════════════════════════
//
// `subscription.sequel` (`_sequel_fact`, `utils/watch_projection.py`, V2-6d)
// — the linked title's own `{id, title, status, next_air}`, published
// UNCONDITIONALLY of whether it is still awaited. `noNextSeasonFound`
// (`watch.js`, unchanged) — true only once nothing at all is known. Reading
// `subscription.sequel` directly (never re-deriving it from `franchise.next`
// client-side) is deliberate: `franchise` names a target id this module has
// no way to resolve to a title on its own (an auto-detected sequel is very
// often UNLISTED), and `sequel` already did that resolution server-side.
//
// This is genuinely a THIRD state `FranchiseNote`'s old two branches never
// drew: a linked sequel that has already started RELEASING/FINISHED reads
// `awaiting: null` (Jordan's ruling: that is "watching," never "awaiting")
// but `sequel` still names it — nameable nowhere on this page until now.
// `FranchiseNote`'s own "announced" branch is UNCHANGED and renders
// alongside this component, never replaced by it — see that component's
// own header.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 MOVIE VS SERIES — "PICK THE RIGHT NOUN" (BRIEF §2 ITEM 1)
// ═══════════════════════════════════════════════════════════════════════════
//
// A film has no "season." `subscription.kind === 'movie'` reads "sequel"
// everywhere this control would otherwise say "next season" — the toggle,
// the search label, and the linked-state line. Every OTHER sentence here
// ("Change," "Remove the link," the confirm step) is noun-agnostic by
// construction and needs no branch.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 SIGNED OUT — THE WHOLE CONTROL, NOT JUST ITS BUTTONS
// ═══════════════════════════════════════════════════════════════════════════
//
// `ActionBar.jsx` (`if (!you.signedIn) return null`) and `AddPanel.jsx`'s
// own `AddShowSection` (`if (!signed_in) return null`) are this page's two
// existing precedents for a REQUEST control's signed-out behaviour: hide
// the whole thing, never a read-only remainder. This control follows the
// identical shape. In practice `/api/watch/titles/:id` itself 401s a
// signed-out caller (`site/functions/api/watch/titles/[subscriptionId].js`),
// so this page never renders AT ALL while signed out — this check exists
// for the same reason `ActionBar`'s does: defensive, and directly testable
// against a fixture the live endpoint would never actually send.

const REMOVE_DONE_TEXT = 'Removed the link.'

/** "TMDB TV, 2022" / "AniList, year unknown" — a small, LOCAL version of
 * `AddPanel.jsx::searchHitLabel`. Not imported: `AddPanel.jsx` is another
 * lane's file (V2-6f2, still running — see this brief's own §6) and this
 * slice's brief lists it as read-only PATTERN, never an import source (only
 * `watchRequests.js` and `useTitleSearch.js` are named for their exports).
 * Four lines of pure formatting, duplicated deliberately rather than
 * coupling this file to a module owned by a lane running concurrently. */
function hitLabel(hit) {
  const source = hit.provider === 'tmdb' ? (hit.kind === 'movie' ? 'TMDB Movie' : 'TMDB TV') : 'AniList'
  const year = typeof hit.year === 'number' && Number.isFinite(hit.year) ? String(hit.year) : 'year unknown'
  return `${source}, ${year}`
}

/** One search result row — poster, title, source/year, "Link this." Never
 * offers a hit `isSelfReferenceHit` flags (see `watchTitle.js`'s own header
 * for the source-id comparison, and why round 2 dropped the original title
 * proxy) or one this build cannot turn into a target at all
 * (`logTargetForSearchHit` returning `null` — an unrecognised
 * provider/kind, the identical shape `AddPanel.jsx::SearchResultRow`
 * already treats as permanently unavailable). */
function LinkResultRow({ hit, onPick }) {
  const target = logTargetForSearchHit(hit)
  if (target === null) return null
  return (
    <div className="d2d-watchtitle__franchiselinkresult" data-testid="franchise-link-result">
      <PosterSlot subscription={{ poster_url: hit.poster }} size="search" />
      <div className="d2d-watchtitle__franchiselinkresultbody">
        <div className="d2d-watchtitle__franchiselinkresulttitle">{hit.title}</div>
        <div className="d2d-micro d2d-watchtitle__franchiselinkresultmeta">{hitLabel(hit)}</div>
        <button
          type="button"
          className="d2d-watchtitle__franchiselinkbtn"
          data-testid="franchise-link-pick"
          onClick={() => onPick(target, hit.title)}
        >
          Link this
        </button>
      </div>
    </div>
  )
}

/** The search box + result list — shared by the very first link and by
 * "Change" (brief §2 item 2: "Change: the same picker"). `onCancel` returns
 * to whatever idle state the caller was already in. */
function LinkSearchPicker({ subscription, isMovie, onPick, onCancel }) {
  const [query, setQuery] = useState('')
  const search = useTitleSearch(query)
  const trimmed = query.trim()
  const results = search.results.filter((hit) => !isSelfReferenceHit(hit, subscription))

  return (
    <div className="d2d-watchtitle__franchiselink" data-testid="franchise-link">
      <label className="d2d-watchtitle__franchiselinksearchlabel">
        <span className="d2d-micro d2d-watchtitle__sectiontitle">
          {isMovie ? 'Search for the sequel' : 'Search for the next season'}
        </span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="A title…"
          className="d2d-watchtitle__franchiselinkinput"
          data-testid="franchise-link-search-input"
        />
      </label>

      {search.status === 'limited' || search.status === 'error' ? (
        <p className="d2d-watchtitle__franchiselinknotice" role="alert" data-testid="franchise-link-search-message">
          {search.message}
        </p>
      ) : null}

      {trimmed.length < MIN_QUERY_LENGTH ? (
        <p className="d2d-micro d2d-watch__note" data-testid="franchise-link-search-hint">
          Type at least two letters.
        </p>
      ) : search.status === 'ready' && results.length === 0 ? (
        <p className="d2d-micro d2d-watch__note" data-testid="franchise-link-search-empty">
          {`Nothing found on TMDB or AniList for “${trimmed}”.`}
        </p>
      ) : null}

      {results.map((hit) => (
        <LinkResultRow key={`${hit.provider}:${hit.kind}:${hit.id}`} hit={hit} onPick={onPick} />
      ))}

      <button
        type="button"
        className="d2d-watchtitle__franchiselinkcancel"
        data-testid="franchise-link-search-cancel"
        onClick={onCancel}
      >
        Cancel
      </button>
    </div>
  )
}

/**
 * The whole "Link the next season" surface — mounted by `FranchiseNote`
 * (`WatchTitle.jsx`), right where its old `/watch link` hint used to sit.
 *
 * `subscription`/`you`/`onChanged` are exactly `Hero`'s own props, passed
 * straight through: `you` is `deriveYou`'s shape (`{signedIn, status}`),
 * `onChanged` is `title.confirm` (`watchTitle.js::useWatchTitle`), the same
 * bounded re-read loop every other write-capable control on this page
 * already threads through (`ActionBar`, the episode taps).
 */
export function FranchiseLinkControl({ subscription, you, onChanged }) {
  const request = useWatchRequest()
  const [searching, setSearching] = useState(false)
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  // What THIS press asked for — the raw payload target for `onRetry`
  // (`lastTargetRef`, `request.ask`'s own body) and the sequel's own title
  // text for both `doneText` and confirming the fresh read below
  // (`askedTitleRef`; `null` names a CLEAR, matching `linkCommand`'s own
  // `null`-means-clear convention, `watchRequests.js`).
  const lastTargetRef = useRef(null)
  const askedTitleRef = useRef(null)

  // P6-281's bounded re-read loop, `FavoriteHeart`'s own pattern
  // (`ActionBar.jsx`) applied to this field: compare the FRESH `sequel`
  // title against what this press asked for, both sides null-coalesced so
  // a genuine clear (`askedTitleRef.current === null`) confirms correctly
  // against a fresh `sequel: null` too.
  const isConfirmed = (shared) => {
    const fresh = subscriptionsOf(shared).find((row) => row.id === subscription?.id)
    return (fresh?.sequel?.title ?? null) === askedTitleRef.current
  }
  const confirmFailed = useConfirmAfterApplied(request, onChanged, isConfirmed)

  const press = (nextTarget, nextTitleText) => {
    lastTargetRef.current = nextTarget
    askedTitleRef.current = nextTitleText
    setSearching(false)
    setConfirmingRemove(false)
    request.ask({ kind: KIND_LINK_SEQUEL, target: subscription.id, payload: { next: nextTarget } })
  }

  // Signed out: the whole control, not a read-only remainder — see the
  // module header. Every hook above still runs unconditionally either way
  // (React's own rule; the early return happens only after them, exactly
  // `WatchTitleBody`'s own shape for its LOADING/NOT_FOUND/… branches).
  if (!you?.signedIn) return null

  const sequel = subscription?.sequel ?? null
  const noneFound = noNextSeasonFound(subscription)
  if (sequel === null && !noneFound) return null

  const isMovie = subscription?.kind === 'movie'

  if (request.status) {
    return (
      <div className="d2d-watchtitle__franchiselink" data-testid="franchise-link">
        <ActStrip
          request={request}
          confirmFailed={confirmFailed}
          testId="franchise-link-status"
          confirmStaleTestId="franchise-link-confirm-stale"
          doneText={
            askedTitleRef.current === null
              ? REMOVE_DONE_TEXT
              : `${isMovie ? 'Sequel' : 'Next season'}: ${askedTitleRef.current}`
          }
          command={linkCommand(subscription?.title, askedTitleRef.current)}
          onRetry={() => press(lastTargetRef.current, askedTitleRef.current)}
        />
      </div>
    )
  }

  if (searching) {
    return (
      <LinkSearchPicker
        subscription={subscription}
        isMovie={isMovie}
        onPick={press}
        onCancel={() => setSearching(false)}
      />
    )
  }

  if (confirmingRemove) {
    // Brief §2 item 2, verbatim: "Remove the link to <title>?" → Remove /
    // Keep. Keep sends nothing — a plain local reset, no request.
    return (
      <div className="d2d-watchtitle__franchiselink" data-testid="franchise-link">
        <p className="d2d-watch__flatline" data-testid="franchise-link-remove-confirm">
          {`Remove the link to ${sequel.title}? The bot won’t look for a replacement automatically — link one again if you want to.`}
        </p>
        <div className="d2d-watchtitle__franchiselinkbtns">
          <button
            type="button"
            className="d2d-watchtitle__franchiselinkbtn"
            data-testid="franchise-link-remove-yes"
            onClick={() => press(null, null)}
          >
            Remove
          </button>
          <button
            type="button"
            className="d2d-watchtitle__franchiselinkbtn"
            data-testid="franchise-link-remove-keep"
            onClick={() => setConfirmingRemove(false)}
          >
            Keep
          </button>
        </div>
      </div>
    )
  }

  if (sequel !== null) {
    return (
      <div className="d2d-watchtitle__franchiselink" data-testid="franchise-link">
        <p className="d2d-watch__flatline" data-testid="franchise-link-current">
          {`${isMovie ? 'Sequel' : 'Next season'}: ${sequel.title}`}
        </p>
        <div className="d2d-watchtitle__franchiselinkbtns">
          <button
            type="button"
            className="d2d-watchtitle__franchiselinkbtn"
            data-testid="franchise-link-change"
            onClick={() => setSearching(true)}
          >
            Change
          </button>
          <button
            type="button"
            className="d2d-watchtitle__franchiselinkbtn"
            data-testid="franchise-link-remove"
            onClick={() => setConfirmingRemove(true)}
          >
            Remove the link
          </button>
        </div>
      </div>
    )
  }

  // `noneFound` is the only way to reach here (the gate above).
  return (
    <div className="d2d-watchtitle__franchiselink" data-testid="franchise-link">
      <button
        type="button"
        className="d2d-watchtitle__franchiselinkbtn"
        data-testid="franchise-link-open"
        onClick={() => setSearching(true)}
      >
        {isMovie ? 'Link the sequel' : 'Link the next season'}
      </button>
    </div>
  )
}

export default FranchiseLinkControl
